import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Overlay2 } from "@blueprintjs/core";

import { HelperFunctions } from "helpers";
import { Button } from "components/buttons";
import './Notifications.css';

const zoomStep = 0.25;
const minZoom = 1;
const maxZoom = 4;

export function ImageViewer(
    {
        onClose = null,
        imageStyle,
        isOpen,
        mediaSource,
        allowZoom = false
    }
) {
    const [zoom, setZoom] = useState(1);
    const [baseSize, setBaseSize] = useState(null);
    const containerRef = useRef(null);

    // Reset zoom state whenever the viewer closes or a different image is shown,
    // so reopening always starts at 1x and with a freshly measured base size.
    useEffect(() => {
        if (!isOpen) {
            setZoom(1);
            setBaseSize(null);
        }
    }, [isOpen]);

    useEffect(() => {
        setZoom(1);
        setBaseSize(null);
    }, [mediaSource]);

    function handleClose() {
        HelperFunctions.executeIfNotNull(onClose);
    }

    function handleImageLoad(event) {
        // Capture the natural rendered size once, on first load — this is the
        // 1x reference that zoom multiplies against.
        if (baseSize === null) {
            setBaseSize({
                width: event.currentTarget.offsetWidth,
                height: event.currentTarget.offsetHeight
            });
        }
    }

    function handleZoomIn() {
        setZoom(z => Math.min(maxZoom, z + zoomStep));
    }

    function handleZoomOut() {
        setZoom(z => Math.max(minZoom, z - zoomStep));
    }

    function handleZoomReset() {
        setZoom(1);
    }

    // Compute the img style up-front (project rule: no nested ternaries in JSX).
    let renderedImageStyle;
    if (zoom > 1 && baseSize !== null) {
        renderedImageStyle = {
            width: (baseSize.width * zoom) + 'px',
            height: (baseSize.height * zoom) + 'px',
            display: 'block'
        };
    }
    else {
        renderedImageStyle = imageStyle;
    }

    return (
        <Overlay2
            isOpen={isOpen}
            onClose={handleClose}
            hasBackdrop={true}
            portalClassName="image-overlay"
            childRef={containerRef}
        >
            <div className="bp6-dialog-container">
                <div ref={containerRef} className="image-viewer-scroll">
                    {mediaSource != null && (
                        <img
                            src={mediaSource}
                            style={renderedImageStyle}
                            onLoad={handleImageLoad}
                            alt=""
                        />
                    )}
                    {allowZoom && (
                        <div className="image-viewer-controls">
                            <Button
                                minimal={true}
                                iconOnly={true}
                                icon="zoom-in"
                                onClick={handleZoomIn}
                                disabled={zoom >= maxZoom}
                            />
                            <Button
                                minimal={true}
                                iconOnly={true}
                                icon="zoom-out"
                                onClick={handleZoomOut}
                                disabled={zoom <= minZoom}
                            />
                            <Button
                                minimal={true}
                                iconOnly={true}
                                icon="reset"
                                onClick={handleZoomReset}
                                disabled={zoom === 1}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Overlay2>
    );
}

ImageViewer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    mediaSource: PropTypes.string,
    onClose: PropTypes.func,
    imageStyle: PropTypes.object,
    allowZoom: PropTypes.bool,
};
